import { useEffect, useState } from "react";
import api from "../lib/api";
import { FadeUp } from "../components/Motion";
import { Calculator, Phone, Mail } from "lucide-react";
import PageMeta from "../components/PageMeta";
import { trackInitiateCheckout } from "../lib/fbpixel";

// Mapping from unit code → human-readable suffix shown in the UI
const UNIT_LABEL = {
  sqft:          "sq ft",
  linft:         "linear ft",
  linft_layers:  "linear ft",
  yard3:         "yard³",
  ton:           "tons",
  each:          "units",
  flat:          "",
  custom:        "",
};

const formatRate = (r) => {
  if (r.unit === "custom") return "Custom quote";
  const range = r.min === r.max ? `$${r.min.toLocaleString()}` : `$${r.min.toLocaleString()}–$${r.max.toLocaleString()}`;
  switch (r.unit) {
    case "sqft":         return `${range} per sq ft`;
    case "linft":        return `${range} per linear ft`;
    case "linft_layers": return `${range} per ft per layer`;
    case "yard3":        return `${range} per yard³`;
    case "ton":          return `${range} per ton`;
    case "each":         return `${range} each`;
    case "flat":         return `${range} flat`;
    default:             return range;
  }
};

export default function Estimator() {
  const [rates, setRates] = useState({});
  const [items, setItems] = useState({}); // { key: { qty, layers } }
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/estimator/rates").then((r) => setRates(r.data)); }, []);

  const setQty = (k, q) => setItems((p) => ({ ...p, [k]: { ...(p[k] || {}), qty: q } }));
  const setLayers = (k, l) => setItems((p) => ({ ...p, [k]: { ...(p[k] || {}), layers: l } }));
  const toggle = (k) => setItems((p) => ({ ...p, [k]: { qty: (p[k]?.qty ? 0 : 1) } }));

  const calc = async () => {
    setLoading(true);
    const payload = {
      items: Object.entries(items)
        .filter(([key, v]) => {
          const rate = rates[key];
          if (!rate) return false;
          if (rate.unit === "custom") return !!v?.qty; // included flag
          return +(v?.qty || 0) > 0;
        })
        .map(([key, v]) => ({
          key,
          quantity: +(v?.qty || 0),
          layers: +(v?.layers || 1),
        })),
    };
    try {
      const r = await api.post("/estimator/calculate", payload);
      setResult(r.data);
      trackInitiateCheckout({
        content_category: "estimator",
        num_items: payload.items.length,
        value: r.data?.high || r.data?.low || undefined,
        currency: "CAD",
      });
    } finally { setLoading(false); }
  };

  const fmt = (n) => "$" + Math.round(n).toLocaleString();

  // Sort rates so per-area items come first, then per-unit, then flat, then custom
  const sortOrder = { sqft: 1, linft: 2, linft_layers: 2, yard3: 3, ton: 4, each: 5, flat: 6, custom: 7 };
  const sortedRates = Object.entries(rates).sort(([, a], [, b]) => (sortOrder[a.unit] || 99) - (sortOrder[b.unit] || 99));

  const inputBase = "w-full bg-[#151515] border border-[#D8D6D0]/20 px-3 py-2 text-sm text-[#F5F3EF] focus:outline-none focus:border-[#F5F3EF]/70";

  return (
    <div data-testid="estimator-page" className="pt-28 md:pt-32 pb-32 px-4 md:px-12 lg:px-16">
      <PageMeta
        title="Landscape Project Estimator — Instant Edmonton Pricing"
        description="Get an instant ballpark estimate for your landscape project in Edmonton. Patios, retaining walls, fences, planting, lighting, and more — calculated using current Alberta market rates."
        path="/estimator"
      />
      <FadeUp>
        <div className="overline text-[#8C1D30] mb-4 flex items-center gap-2"><Calculator className="w-4 h-4" /> Project Estimator</div>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-none max-w-5xl">
          Approximate budget. <span className="italic text-[#D8D6D0]">Instantly.</span>
        </h1>
        <p className="text-[#D8D6D0]/60 mt-8 max-w-2xl text-lg">
          Select the elements of your project for a market-range estimate based on NorthCrest's current pricing. Final pricing requires a site visit.
        </p>
      </FadeUp>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <FadeUp className="lg:col-span-8">
          <div className="nc-card p-5 md:p-12">
            <div className="space-y-6">
              {sortedRates.map(([k, r]) => {
                const item = items[k] || {};
                return (
                  <div key={k} className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-[#D8D6D0]/10">
                    <div className="flex-1">
                      <div className="font-serif-display text-xl">{r.label}</div>
                      <div className="overline text-[#D8D6D0]/50 mt-1">{formatRate(r)}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Numeric quantity for measurable units */}
                      {["sqft", "linft", "linft_layers", "yard3", "ton", "each"].includes(r.unit) && (
                        <>
                          <input
                            data-testid={`qty-${k}`}
                            type="number"
                            min="0"
                            step={r.unit === "yard3" || r.unit === "ton" ? "0.5" : "1"}
                            placeholder="0"
                            value={item.qty || ""}
                            onChange={(e) => setQty(k, e.target.value)}
                            className={`${inputBase} w-24`}
                          />
                          <span className="text-xs text-[#D8D6D0]/50 whitespace-nowrap">{UNIT_LABEL[r.unit]}</span>
                          {r.unit === "linft_layers" && (
                            <>
                              <span className="text-xs text-[#D8D6D0]/40 mx-1">×</span>
                              <input
                                data-testid={`layers-${k}`}
                                type="number"
                                min="1"
                                step="1"
                                placeholder="1"
                                value={item.layers || ""}
                                onChange={(e) => setLayers(k, e.target.value)}
                                className={`${inputBase} w-16`}
                              />
                              <span className="text-xs text-[#D8D6D0]/50 whitespace-nowrap">layers</span>
                            </>
                          )}
                        </>
                      )}

                      {/* Yes/No toggle for flat-fee items */}
                      {r.unit === "flat" && (
                        <button
                          data-testid={`include-${k}`}
                          onClick={() => toggle(k)}
                          className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors border ${
                            item.qty ? "bg-[#F5F3EF] text-[#151515] border-[#F5F3EF]" : "border-[#D8D6D0]/20 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/60"
                          }`}
                        >
                          {item.qty ? "Included" : "Add"}
                        </button>
                      )}

                      {/* Custom-quote items */}
                      {r.unit === "custom" && (
                        <button
                          data-testid={`include-${k}`}
                          onClick={() => toggle(k)}
                          className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors border ${
                            item.qty ? "bg-[#0B2B1F] text-[#F5F3EF] border-[#F5F3EF]/40" : "border-[#D8D6D0]/20 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/60"
                          }`}
                        >
                          {item.qty ? "Request quote ✓" : "Request quote"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              data-testid="estimator-calc"
              onClick={calc}
              disabled={loading}
              className="mt-8 px-8 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-widest uppercase disabled:opacity-50"
            >
              {loading ? "Calculating…" : "Calculate Estimate"}
            </button>
          </div>
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-4">
          <div className="nc-card p-8 lg:sticky lg:top-32">
            <div className="overline text-[#D8D6D0]/50 mb-4">Estimated Range</div>
            {result ? (
              <div data-testid="estimator-result">
                {(result.low > 0 || result.high > 0) ? (
                  <div className="font-serif-display text-3xl md:text-4xl leading-none">
                    {fmt(result.low)} <span className="text-[#D8D6D0]/50">–</span><br/>{fmt(result.high)}
                  </div>
                ) : (
                  <div className="font-serif-display text-2xl text-[#D8D6D0]/70">Select items above</div>
                )}

                {result.has_custom && (
                  <div className="mt-4 pt-4 border-t border-[#D8D6D0]/10 text-xs text-[#D8D6D0]/70">
                    + Custom items require a site visit for accurate quote
                  </div>
                )}

                <p className="text-xs text-[#D8D6D0]/50 mt-6">{result.disclaimer}</p>

                {result.breakdown && result.breakdown.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-[#D8D6D0]/10 space-y-2 text-sm">
                    {result.breakdown.map((b) => (
                      <div key={b.key} className="flex justify-between text-[#D8D6D0]/70 gap-3">
                        <span className="flex-1 truncate">{b.label}</span>
                        <span className="whitespace-nowrap">
                          {b.custom ? "Custom" : (b.low === b.high ? fmt(b.low) : `${fmt(b.low)}–${fmt(b.high)}`)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Always-visible CTAs */}
                <div className="mt-8 pt-6 border-t border-[#D8D6D0]/10 space-y-3">
                  <a href="tel:+17806557604" className="flex items-center justify-center gap-2 px-4 py-3 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-xs uppercase tracking-widest">
                    <Phone className="w-4 h-4" /> Book Site Visit
                  </a>
                  <a href="mailto:northcrestedmonton@gmail.com" className="flex items-center justify-center gap-2 px-4 py-3 border border-[#D8D6D0]/30 text-[#F5F3EF] text-xs uppercase tracking-widest hover:bg-[#F5F3EF] hover:text-[#151515] transition-colors">
                    <Mail className="w-4 h-4" /> Email Quote Request
                  </a>
                </div>
              </div>
            ) : (
              <div className="text-sm text-[#D8D6D0]/50">Add items and calculate to see your range.</div>
            )}
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
